import {
  CanonicalSlideType,
  MetricKind,
  QualSlideNarrative,
} from "../reporting";

/**
 * Runtime lists of the reporting vocabularies whose owners are Python
 * `Literal`s in `apps/python-api/models/questionnaire/contracts.py`
 * (`MetricKind`, `CanonicalSlideType`, `QualSlideNarrative.slideType`).
 *
 * The unions come from the generated projection in `../reporting`, which is
 * types only, so each runtime list here is keyed by a `Record<Union, true>`:
 * the compiler rejects a missing member and an extra one, and a member added in
 * Python fails type-checking here until the list follows. Order is the owner's
 * `Literal` order (the exported schema's `enum` order). This module imports
 * nothing at runtime, so validators can read it at load time without an import
 * cycle through `./index`.
 */

/** The qualitative subset of `CanonicalSlideType`, owned by `QualSlideNarrative.slideType`. */
export type QualitativeSlideType = Extract<
  CanonicalSlideType,
  QualSlideNarrative["slideType"]
>;

function listOf<Union extends string>(
  members: Record<Union, true>,
): ReadonlyArray<Union> {
  return Object.freeze(Object.keys(members) as Array<Union>);
}

export const METRIC_KINDS = listOf<MetricKind>({
  mean: true,
  median: true,
  distribution: true,
  share: true,
  top_box: true,
  favorable_share: true,
  nps_like: true,
  normalized_score: true,
  composite_score: true,
  ranking_weighted_score: true,
  qual_theme_summary: true,
  qual_keywords: true,
  qual_quotes: true,
});

export const CANONICAL_SLIDE_TYPES = listOf<CanonicalSlideType>({
  cover: true,
  executive_summary: true,
  recommendations: true,
  outro: true,
  single_select_choice_bars: true,
  multi_select_choice_bars: true,
  rating_likert: true,
  continuous_density: true,
  qual_positive_themes: true,
  qual_negative_themes: true,
  qual_descriptive_themes: true,
  matrix_rating: true,
  matrix_categorical_bars: true,
  quadrant_map: true,
  ranking_weighted: true,
  podium: true,
  lp_composite_matrix: true,
  lp_scorecard_matrix: true,
  lp_five_second_test: true,
  ad_scorecard_matrix: true,
  price_sensitivity: true,
  evidence_single: true,
  evidence_rail: true,
  evidence_pair: true,
  evidence_multiples: true,
  metric_callout: true,
  respondent_feature: true,
});

export const QUALITATIVE_SLIDE_TYPES = listOf<QualitativeSlideType>({
  qual_positive_themes: true,
  qual_negative_themes: true,
  qual_descriptive_themes: true,
});

export function isQualitativeSlideType(
  slideType: unknown,
): slideType is QualitativeSlideType {
  return (
    typeof slideType === "string" &&
    (QUALITATIVE_SLIDE_TYPES as ReadonlyArray<string>).includes(slideType)
  );
}
